/**
 * Generic async-chunking framework (Story 19.14, Task 2)
 *
 * Extracted to a shared location so multiple integrations that span > N days
 * per provider-side report can chunk into ≤ N-day windows, run them through a
 * caller-supplied `submitChunk()` function, and aggregate the results within a
 * wall-clock budget.
 *
 * Reddit Ads (Story 19.11) implements its OWN parallel-fan-out chunker in
 * `packages/integrations/src/reddit/analytics.ts → runRedditAdsAsyncChunked`
 * because it needs cross-chunk row-level dedup. Amazon Ads (Story 19.14) uses
 * THIS helper because each chunk is an independent Amazon report submission
 * with its own poll loop; row-level dedup is unnecessary because Amazon's
 * `startDate`/`endDate` boundaries are exclusive at the day level (no overlap).
 *
 * Design constraints:
 *   - Sequential chunk execution (NOT parallel) — keeps consumption of the
 *     15-concurrent-PENDING/PROCESSING-reports-per-account hard cap predictable.
 *   - Wall-clock budget enforcement — partial results returned with
 *     `chunksCompleted < chunksTotal` when budget runs out, paired with a
 *     retryable signal so the caller can resume on a subsequent invocation.
 *   - Pure-function date math — no side effects, no global state.
 */

// ============================================================================
// Types
// ============================================================================

/** Configuration for a chunked-aggregation run. */
export interface ChunkConfig {
  /** Maximum days per single chunk (inclusive of both boundaries). */
  readonly maxDaysPerChunk: number;
  /**
   * Wall-clock budget across the WHOLE chunked run, in milliseconds. When the
   * elapsed time exceeds this budget after a chunk completes, the loop stops
   * and returns partial results.
   */
  readonly timeBudgetMs: number;
}

/** A single chunk's date window — inclusive of both boundaries. */
export interface ChunkWindow {
  readonly startDate: Date;
  readonly endDate: Date;
}

/** Successful chunk-and-aggregate result. */
export interface ChunkAndAggregateOk<TRecord> {
  readonly success: true;
  readonly records: TRecord[];
  readonly warnings: string[];
  readonly chunksCompleted: number;
  readonly chunksTotal: number;
  readonly partial: boolean;
}

/** Failed chunk-and-aggregate result (one of the chunks emitted a fatal error). */
export interface ChunkAndAggregateErr<TRecord> {
  readonly success: false;
  readonly errorMessage: string;
  readonly errorCode?: string;
  readonly chunksCompleted: number;
  readonly chunksTotal: number;
  /**
   * Records aggregated from chunks that succeeded BEFORE the fatal failure —
   * callers can choose to surface them as partial data or discard them.
   */
  readonly partialRecords: TRecord[];
  readonly warnings: string[];
}

export type ChunkAndAggregateResult<TRecord> =
  | ChunkAndAggregateOk<TRecord>
  | ChunkAndAggregateErr<TRecord>;

/** What `submitChunk` returns for a single chunk. */
export type ChunkResult<TRecord> =
  | {
      readonly kind: "ok";
      readonly records: TRecord[];
      readonly warnings?: string[];
    }
  | {
      readonly kind: "error";
      readonly message: string;
      readonly code?: string;
      readonly fatal: boolean;
    };

// ============================================================================
// Pure date helpers
// ============================================================================

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function startOfUtcDay(d: Date): Date {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  );
}

function addDaysUtc(d: Date, days: number): Date {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days)
  );
}

/**
 * Inclusive day count between two dates (UTC), e.g. same-day → 1.
 */
export function daysInclusiveUtc(start: Date, end: Date): number {
  const startMs = startOfUtcDay(start).getTime();
  const endMs = startOfUtcDay(end).getTime();
  return Math.round((endMs - startMs) / MS_PER_DAY) + 1;
}

// ============================================================================
// Chunk window generator (pure)
// ============================================================================

/**
 * Generate fixed-size chunk windows across `[range.startDate, range.endDate]`
 * (inclusive on both ends, day-aligned in UTC).
 *
 * Examples (with `maxDaysPerChunk: 31`):
 *   - 90-day input → 3 chunks (31 + 31 + 28 days)
 *   - 31-day input → 1 chunk (no chunking overhead)
 *   - 32-day input → 2 chunks (31 + 1 day)
 *
 * Throws if `endDate < startDate` or `maxDaysPerChunk <= 0`.
 */
export function generateChunkWindows(
  range: ChunkWindow,
  maxDaysPerChunk: number
): ChunkWindow[] {
  if (!Number.isFinite(maxDaysPerChunk) || maxDaysPerChunk <= 0) {
    throw new Error(
      `generateChunkWindows: maxDaysPerChunk must be > 0, got ${maxDaysPerChunk}`
    );
  }
  if (
    !(range.startDate instanceof Date) ||
    !(range.endDate instanceof Date) ||
    Number.isNaN(range.startDate.getTime()) ||
    Number.isNaN(range.endDate.getTime())
  ) {
    throw new Error("generateChunkWindows: range contains invalid Date values");
  }
  if (range.startDate.getTime() > range.endDate.getTime()) {
    throw new Error(
      "generateChunkWindows: range.startDate must be on or before range.endDate"
    );
  }

  const totalDays = daysInclusiveUtc(range.startDate, range.endDate);
  if (totalDays <= maxDaysPerChunk) {
    return [
      {
        startDate: startOfUtcDay(range.startDate),
        endDate: startOfUtcDay(range.endDate),
      },
    ];
  }

  const chunks: ChunkWindow[] = [];
  let cursor = startOfUtcDay(range.startDate);
  const lastDay = startOfUtcDay(range.endDate);
  while (cursor.getTime() <= lastDay.getTime()) {
    const candidateEnd = addDaysUtc(cursor, maxDaysPerChunk - 1);
    const chunkEnd =
      candidateEnd.getTime() > lastDay.getTime() ? lastDay : candidateEnd;
    chunks.push({ startDate: cursor, endDate: chunkEnd });
    cursor = addDaysUtc(chunkEnd, 1);
  }
  return chunks;
}

// ============================================================================
// Sequential chunk-and-aggregate
// ============================================================================

/**
 * Execute `submitChunk` sequentially across the chunk windows of a date range,
 * aggregate the records, and respect the `timeBudgetMs` wall-clock budget.
 *
 * Behavior:
 *   - Returns `partial: true` (with `chunksCompleted < chunksTotal`) when the
 *     wall-clock budget runs out before all chunks complete. The caller treats
 *     this as a retryable warning and resumes on the next invocation.
 *   - On a fatal chunk error, stops the loop and returns
 *     `ChunkAndAggregateErr` with whatever records were aggregated up to that
 *     point.
 *   - Non-fatal chunk errors (`fatal: false`) are appended to the `warnings`
 *     array and the loop continues with the next chunk.
 *
 * @param range          — overall date range to chunk
 * @param config         — chunk size + wall-clock budget
 * @param submitChunk    — caller-supplied per-chunk worker
 * @param now            — clock injection for tests; defaults to `Date.now`
 */
export async function chunkAndAggregate<TRecord>(
  range: ChunkWindow,
  config: ChunkConfig,
  submitChunk: (chunk: ChunkWindow) => Promise<ChunkResult<TRecord>>,
  now: () => number = Date.now
): Promise<ChunkAndAggregateResult<TRecord>> {
  const start = now();
  const chunks = generateChunkWindows(range, config.maxDaysPerChunk);
  const records: TRecord[] = [];
  const warnings: string[] = [];
  let chunksCompleted = 0;

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i] as ChunkWindow;
    const elapsed = now() - start;
    if (elapsed > config.timeBudgetMs) {
      // Budget exhausted BEFORE attempting this chunk — stop, return partial.
      return {
        success: true,
        records,
        warnings,
        chunksCompleted,
        chunksTotal: chunks.length,
        partial: true,
      };
    }

    const r = await submitChunk(chunk);
    if (r.kind === "ok") {
      for (const rec of r.records) records.push(rec);
      if (r.warnings) {
        for (const w of r.warnings) warnings.push(w);
      }
      chunksCompleted++;
      continue;
    }

    // r.kind === "error"
    if (r.fatal) {
      return {
        success: false,
        errorMessage: r.message,
        ...(r.code !== undefined ? { errorCode: r.code } : {}),
        chunksCompleted,
        chunksTotal: chunks.length,
        partialRecords: records,
        warnings,
      };
    }
    // Non-fatal — record warning + continue.
    warnings.push(`chunk ${i + 1}/${chunks.length}: ${r.message}`);
    chunksCompleted++;
  }

  return {
    success: true,
    records,
    warnings,
    chunksCompleted,
    chunksTotal: chunks.length,
    partial: false,
  };
}
